import React, { useCallback, useMemo } from 'react';
import { getMessage } from 'util/locale';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'app/hooks';

import { exportTypes } from 'constants/values';
import {
  closePreferredDeviceModal,
  selectPreferredDevice,
  selectMeasurementType,
  selectMeasurementDistance,
  toggleDriveModal
} from 'current/uiwidgets';
import { savePreferredDevice, saveFormat } from 'devices';
import { getDevicesForScriptType } from 'devices/selectors';
import {
  getActivePreferredDevice,
  getActiveMeasurementDistance,
  getActiveScript,
  preferredDeviceModalIsOpen,
  getPreferredDeviceModalExportType
} from 'current/selectors';
import { getOptionSelected } from '../../current/uiwidgets/selectors';
import { setExportParams } from '../../export-selector/index';
import { downloadScriptsForExportType } from 'scripts';
import { formatNumberStripDelimiters } from 'util/formatters/numberFormatter';
import { inferUnit } from 'util/unitConversion';
import { staraShapefileId, ROW_SPACING_MIN, ROW_SPACING_MAX } from '../../constants/devices';

// components
import BaseModal from 'common/modals/BaseModal';
import { Dropdown, TextInput } from '@tcc/crate';
import { activityTypes } from 'constants/activities';

const baseString = 'vra-preferred-device-selector-container';
const classString = classNames({
  [`${baseString}`]: true
});
const PreferredDeviceModal = () => {
  const activePreferredDevice = useAppSelector(getActivePreferredDevice);
  const activePreferredDeviceId = activePreferredDevice?.value;
  const activePreferredDeviceName = activePreferredDevice?.label;
  const activeMeasurementDistance = useAppSelector(getActiveMeasurementDistance);
  const saveOption = useAppSelector(getOptionSelected);
  const devices = useAppSelector(getDevicesForScriptType);
  const exportType = useAppSelector(getPreferredDeviceModalExportType);
  const isOpen = useAppSelector(preferredDeviceModalIsOpen);
  const script = useAppSelector(getActiveScript);
  const dispatch = useAppDispatch();

  const isSingleExport = exportType === exportTypes.SINGLE;
  const isStaraDevice = activePreferredDeviceId === staraShapefileId;
  const isPlantingScript = script?.['activity-type'] === activityTypes.PLANTING;
  const isRowSpacingValid
    = activeMeasurementDistance > ROW_SPACING_MIN && activeMeasurementDistance < ROW_SPACING_MAX;

  const showRowSpacing = (!isSingleExport || (isPlantingScript && isSingleExport))
    && isStaraDevice;
  const isModalSubmitValid = showRowSpacing ? isRowSpacingValid : activePreferredDeviceId;

  const options = useMemo(
    () => devices.length
      ? devices
      .toSorted((a, b) => a.label.localeCompare(b.label))
      .map(({ label: value, value: id }) => ({ value, id: `${id}-${value}` }))
      : [], [devices]
  );

  const exportMessageKey = isSingleExport
    ? 'title-text__select-shapefile'
    : 'title-text__select-shapefile-for-bulk';
  const exportMessageHeader = 'title-text__select-option';

  const onCancelPreferredDeviceModal = useCallback(() => dispatch(closePreferredDeviceModal()), [dispatch]);

  const onSelectDevice = useCallback((selected) => {
    dispatch(selectPreferredDevice(selected));
    dispatch(selectMeasurementType());
    dispatch(selectMeasurementDistance());
  }, [dispatch]);

  const onSelectMeasurementDistance = useCallback(
    (selected) => dispatch(selectMeasurementDistance(selected)),
    [dispatch]
  );

  const onSavePreferredDeviceModal = useCallback(() => {
    dispatch(savePreferredDevice(activePreferredDevice));
    dispatch(downloadScriptsForExportType({ exportType, scriptId: script.id }));
  }, [activePreferredDevice, dispatch, exportType, script?.id]);

  const onSelectSendToDrive = useCallback(() => {
    const scriptData = {
      scriptName: script.name,
      deviceLabel: activePreferredDeviceName
    };
    dispatch(setExportParams());
    dispatch(saveFormat(scriptData));
    dispatch(toggleDriveModal(true));
    dispatch(closePreferredDeviceModal());
  }, [activePreferredDeviceName, dispatch, script?.name]);

  return isOpen ? (
    <BaseModal
      header={getMessage(saveOption ? exportMessageHeader : exportMessageKey)}
      isInvalid={!isModalSubmitValid}
      onCancel={onCancelPreferredDeviceModal}
      onSave={onSavePreferredDeviceModal}
      onSendToDrive={onSelectSendToDrive}
      saveOption={saveOption}
      saveText={getMessage('button-text__select-shapefile')}
    >
      <section className={classString}>
        <Dropdown
          label={getMessage('label-text__select-shapefile')}
          options={options}
          optionsCanWrap={true}
          setValue={(selectedDeviceName: string) => {
            const newSelectedDevice = Object.values(devices).find(device => selectedDeviceName === device.label);
            onSelectDevice(newSelectedDevice);
          }}
          value={activePreferredDeviceName}
        />
        {showRowSpacing && <div>
          {<div className={`${baseString}__stara-container`}>
            <TextInput
              className={`${baseString}__input`}
              testId={`${baseString}__input`}
              maxLength={3}
              onChange={(e) => {
                const newDistance = formatNumberStripDelimiters(e.target.value || 0);
                onSelectMeasurementDistance(newDistance);
              }}
              placeholder='0'
              showReset={false}
              value={isRowSpacingValid ? activeMeasurementDistance : ''}
              label={getMessage('label-text__row-spacing', { unit: inferUnit('soil-depth').symbol.quantitySpecific })}
            />
          </div>}
        </div>}
      </section>
    </BaseModal>
  ) : null;
};

export default PreferredDeviceModal;
